/* NSI mobile menu: sliding two-level panels (MakeItScale, Sep 2026).
 *
 * The theme's own NavMobile (superstore.js:36055) still owns opening and closing the
 * drawer; this only drives what happens INSIDE it. The old accordion
 * (NavMobileSubMenus, superstore.js:35931) binds to `.navmenu-link-parent` and
 * `.navmenu-button`, neither of which exists in this markup, so it never engages.
 *
 * Split controls (Zac, 2026-09-10): a level-1 parent row is an <a> that navigates
 * plus a sibling <button class="nsi-mnav__chev"> that opens the panel. Nothing in
 * here may match the <a>, or the category link stops working again.
 *
 * IMPORTANT: never use the class name `visible` on anything inside the drawer panel.
 * The theme's Accordion.closeOpen() does `$panel.find('.visible').removeClass('visible')`
 * on every drawer close and would silently wipe our panel state.
 *
 * 30 Sep 2026 (speed, Claude): the level-2 panels are no longer in the page HTML. They are
 * fetched from [data-nsi-mnav-slot]'s data-nsi-mnav-src (templates/search.nsimnav.liquid)
 * and inserted where the slot sits: after window load on screens that show the menu button
 * (desktops never download them), straight away when the menu button is pressed, or on a
 * chevron tap if that comes first (the chevron shows .is-loading meanwhile). If the fetch
 * fails, the chevron falls back to the category page, like tapping the row name.
 * Rollback: site-speed-2026-09-30-lcp/.
 */
(function () {
  var root = document.querySelector('[data-nsi-mnav-root]');
  if (!root) return;

  var drawer = root.closest('[data-nsi-mnav]');
  var backBtn = drawer ? drawer.querySelector('[data-nsi-mnav-back]') : null;
  var panels = {};
  var openers = {};
  var stack = ['root'];
  var ownsHistory = false;

  // Runs at start-up and again when the deferred panels arrive; only adds what is new.
  function register() {
    Array.prototype.forEach.call(root.querySelectorAll('[data-nsi-mnav-panel]'), function (p) {
      var key = p.getAttribute('data-nsi-mnav-panel');
      if (panels[key]) return;
      panels[key] = p;
      // Markup ships `hidden` so the panels stay collapsed if this script never runs.
      // Hand control to CSS now that it has.
      if (key !== 'root') p.hidden = false;
    });
  }
  register();
  // Only the chevron BUTTON carries data-nsi-mnav-open; the row's <a> is a plain
  // link now (nsi-mnav-row.liquid), which is what lets a tap on the name navigate.
  Array.prototype.forEach.call(root.querySelectorAll('button[data-nsi-mnav-open]'), function (b) {
    openers[b.getAttribute('data-nsi-mnav-open')] = b;
  });

  function current() { return stack[stack.length - 1]; }

  function paint() {
    var id = current();
    Object.keys(panels).forEach(function (key) {
      var p = panels[key];
      var on = key === id;
      p.classList.toggle('is-current', on);
      if (key === 'root') return;          // root sits underneath, always laid out
      if (on) p.removeAttribute('inert'); else p.setAttribute('inert', '');
    });
    Object.keys(openers).forEach(function (key) {
      openers[key].setAttribute('aria-expanded', key === id ? 'true' : 'false');
    });
    if (backBtn) backBtn.hidden = id === 'root';
    drawer.classList.toggle('nsi-mnav-deep', id !== 'root');
  }

  function goTo(id) {
    if (!panels[id] || current() === id) return;
    var wasRoot = current() === 'root';
    stack.push(id);
    panels[id].scrollTop = 0;              // a new panel always opens at the top
    paint();
    // Android hardware back should step out of the panel, not off the site.
    if (wasRoot && window.history && window.history.pushState) {
      try { window.history.pushState({ nsiMnav: true }, ''); ownsHistory = true; } catch (e) {}
    }
    var focusable = panels[id].querySelector('a, button');
    if (focusable) focusable.focus({ preventScroll: true });
  }

  function resetToRoot() {
    if (stack.length === 1) return;
    stack = ['root'];
    paint();                               // root keeps its own scrollTop, deliberately
  }

  function goBack() {
    if (stack.length === 1) return;
    if (ownsHistory) {
      // 30 Sep 2026 fix: leave ownsHistory set so the popstate handler below resets the menu.
      // It used to be cleared first, so popstate ignored the event and the in-drawer back
      // arrow (and Esc) needed TWO taps. Safety net if popstate never arrives.
      window.history.back();
      setTimeout(function () { if (ownsHistory) { ownsHistory = false; resetToRoot(); } }, 400);
      return;
    }
    resetToRoot();
    if (backBtn) backBtn.blur();
  }

  // ---- Deferred level-2 panels (30 Sep 2026) ----
  var slot = root.querySelector('[data-nsi-mnav-slot]');
  var loading = null;                      // the in-flight fetch, if any

  function drawerOpen() {
    return !!drawer && (drawer.classList.contains('visible') || drawer.classList.contains('animating-in')) &&
      !drawer.classList.contains('animating-out');
  }

  function loadPanels(lowPriority) {
    if (!slot) return Promise.resolve();   // already in (or server-rendered markup)
    if (loading) return loading;
    var opts = { credentials: 'same-origin' };
    if (lowPriority) opts.priority = 'low';
    loading = fetch(slot.getAttribute('data-nsi-mnav-src'), opts)
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var tpl = document.createElement('template');
        tpl.innerHTML = html;              // inert: nothing loads until inserted
        var box = tpl.content.querySelector('[data-nsi-mnav-deferred]');
        if (!box || !box.querySelector('[data-nsi-mnav-panel]')) throw new Error('no panels');
        if (!slot) return;                 // another call got there first
        while (box.firstChild) slot.parentNode.insertBefore(box.firstChild, slot);
        slot.parentNode.removeChild(slot);
        slot = null;
        register();
        paint();
      });
    loading.catch(function () { loading = null; });   // let the next tap retry
    return loading;
  }

  // Delegated to the chevron BUTTON only. This listener used to match the whole
  // row and preventDefault() unconditionally, which ate the category link on
  // every tap; scoping the selector to `button[...]` makes the preventDefault
  // harmless (it only stops the button, never the sibling <a>).
  root.addEventListener('click', function (e) {
    var opener = e.target.closest ? e.target.closest('button[data-nsi-mnav-open]') : null;
    if (!opener || !root.contains(opener)) return;
    var id = opener.getAttribute('data-nsi-mnav-open');
    if (panels[id]) {
      e.preventDefault();
      goTo(id);
      return;
    }
    if (!slot) return;                     // panels are in and this row has none: leave it alone
    e.preventDefault();
    opener.classList.add('is-loading');
    opener.setAttribute('aria-busy', 'true');
    var done = function () {
      opener.classList.remove('is-loading');
      opener.removeAttribute('aria-busy');
    };
    loadPanels(false).then(function () {
      done();
      if (panels[id] && drawerOpen()) goTo(id);
    }, function () {
      done();
      // Could not fetch the panels: do what tapping the row name does.
      var row = opener.parentNode ? opener.parentNode.querySelector('a[href]') : null;
      if (row && drawerOpen()) window.location.href = row.href;
    });
  });

  // Warm the panels once the page has finished loading, but only where the phone menu is
  // actually used: the menu button is display:none from 1024px up, so desktops skip this.
  function warm() {
    var t = document.querySelector('[data-menu-toggle]');
    if (!slot || !t || !t.getClientRects().length) return;
    var go = function () { loadPanels(true).catch(function () {}); };
    if (window.requestIdleCallback) window.requestIdleCallback(go, { timeout: 3000 });
    else setTimeout(go, 1200);
  }
  if (document.readyState === 'complete') warm();
  else window.addEventListener('load', warm);

  // Pressing the menu button means the panels are about to be needed: fetch right away.
  document.addEventListener('pointerdown', function (e) {
    if (slot && e.target.closest && e.target.closest('[data-menu-toggle]')) loadPanels(false).catch(function () {});
  }, { capture: true, passive: true });

  if (backBtn) backBtn.addEventListener('click', function (e) { e.preventDefault(); goBack(); });

  window.addEventListener('popstate', function () {
    if (!ownsHistory) return;
    ownsHistory = false;
    resetToRoot();
  });

  // Capture phase so this beats the theme's own Esc handler (superstore.js:36136):
  // inside a panel, Esc should step back, not close the whole drawer.
  window.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.keyCode !== 27) return;
    if (current() === 'root') return;
    e.stopImmediatePropagation();
    e.preventDefault();
    goBack();
  }, true);

  // Closing the drawer from level 2 must leave the menu at level 1 for next time.
  var closer = drawer ? drawer.querySelector('[data-mobile-nav-close]') : null;
  if (closer) closer.addEventListener('click', function () {
    if (ownsHistory) { ownsHistory = false; window.history.back(); }
    setTimeout(resetToRoot, 350);
  });

  // Tap-outside-to-close only became reachable on tablet (the drawer stopped
  // covering the whole viewport, so the overlay got its hit-testing back). That
  // path runs the theme's NavMobile._close, NOT the X button, so it needs the
  // same reset or the drawer reopens on whatever level-2 panel was last open.
  var overlay = drawer ? drawer.querySelector('[data-mobile-nav-overlay]') : null;
  if (overlay) overlay.addEventListener('click', function () {
    if (ownsHistory) { ownsHistory = false; window.history.back(); }
    setTimeout(resetToRoot, 350);
  });

  paint();
})();
